import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { GNRE_STATUSES, type GnreList, type GnreListItem, type GnreListQuery } from "@cx/contracts";
import { BRAZIL_UFS, GNRE_LOTE_MAX_GUIDES, formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { downloadApiFile } from "../../download";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { GnreSettingsDialog } from "./GnreDialogs";
import { GNRE_KIND_LABEL, GNRE_REVENUE_LABEL, GNRE_STATUS_LABEL, GNRE_STATUS_TONE } from "./labels";

type StatusFilter = GnreListQuery["status"];

/** GNRE pendente ou emitida entra no XML do lote (DARE-SP é emitido no portal da SEFAZ-SP). */
export const inLote = (g: Pick<GnreListItem, "kind" | "status">) =>
  g.kind === "gnre" && (g.status === "pending" || g.status === "issued");

/**
 * Vendas › Guias GNRE/DARE (T2.11, RF-VEN-11): guias de ICMS-ST/DIFAL/FCP devidos a outra UF, geradas pela NF-e
 * autorizada. Filtros por situação e UF; seleção de GNRE para baixar o XML do lote (até 50) e enviar no Portal GNRE.
 */
export function GnreListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const [status, setStatus] = useState<StatusFilter>("open");
  const [uf, setUf] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settings, setSettings] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ["gnre", companyId, "list", status, uf],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<GnreList>(
        `/gnre?${new URLSearchParams({
          status,
          ...(uf ? { uf } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const ids = rows.filter((r) => selected.has(r.id) && inLote(r)).map((r) => r.id);
  const xml = useMutation({
    mutationFn: () => downloadApiFile("/gnre/xml", companyId, "lote-gnre.xml", { ids }),
    onSuccess: () => toast("success", "XML do lote baixado. Envie-o no Portal GNRE."),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <>
      <ContentHeader
        title="Guias GNRE/DARE"
        breadcrumb={["Vendas", "Guias GNRE/DARE"]}
        actions={
          <div className="d-flex flex-wrap gap-2">
            {can(perms, "gnre", "update") && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => setSettings(true)}>
                Configurar
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={ids.length === 0 || ids.length > GNRE_LOTE_MAX_GUIDES || xml.isPending}
              title={
                ids.length > GNRE_LOTE_MAX_GUIDES ? `Até ${GNRE_LOTE_MAX_GUIDES} guias por lote` : undefined
              }
              onClick={() => xml.mutate()}
            >
              Baixar XML do lote{ids.length ? ` (${ids.length})` : ""}
            </button>
          </div>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="gnre-status">
              Situação
            </label>
            <select
              id="gnre-status"
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setSelected(new Set());
              }}
            >
              <option value="open">Em aberto (pendentes e emitidas)</option>
              <option value="all">Todas as situações</option>
              {GNRE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {GNRE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="visually-hidden" htmlFor="gnre-uf">
              UF
            </label>
            <select id="gnre-uf" className="form-select" value={uf} onChange={(e) => setUf(e.target.value)}>
              <option value="">Todas as UFs</option>
              {BRAZIL_UFS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma guia"
            description="Gere as guias na NF-e autorizada para consumidor final ou com ICMS-ST de outra UF (painel Guias GNRE/DARE da nota)."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Guias GNRE/DARE">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="visually-hidden">Selecionar</span>
                    </th>
                    <th scope="col">Guia</th>
                    <th scope="col">NF-e</th>
                    <th scope="col">Destinatário</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {inLote(r) && (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            aria-label={`Selecionar guia ${GNRE_REVENUE_LABEL[r.revenue]} ${r.uf} da NF-e ${r.invoiceNumber}`}
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                          />
                        )}
                      </td>
                      <td className="text-nowrap">
                        <Link to="/vendas/gnre/$id" params={{ id: r.id }}>
                          {GNRE_KIND_LABEL[r.kind]} {GNRE_REVENUE_LABEL[r.revenue]} — {r.uf}
                        </Link>
                        <span className="text-muted small ms-2">{r.revenueCode}</span>
                        {r.environment === 2 && (
                          <span className="badge text-bg-warning ms-2" title="NF-e sem valor fiscal">
                            Homologação
                          </span>
                        )}
                      </td>
                      <td className="text-nowrap">
                        <Link to="/vendas/notas-fiscais/$id" params={{ id: r.fiscalDocumentId }}>
                          {r.invoiceNumber} / {r.invoiceSeries}
                        </Link>
                        {r.invoiceCanceled && (
                          <span
                            className="badge text-bg-danger ms-2"
                            title="A NF-e foi cancelada depois da guia"
                          >
                            NF-e cancelada
                          </span>
                        )}
                      </td>
                      <td>{r.recipientName ?? "—"}</td>
                      <td>{formatDateBR(r.dueDate)}</td>
                      <td className="text-end">{formatBRL(r.amountCents)}</td>
                      <td>
                        <StatusBadge tone={GNRE_STATUS_TONE[r.status]}>
                          {GNRE_STATUS_LABEL[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="gnre-summary">
                  {summary.count} {summary.count === 1 ? "guia" : "guias"} · em aberto{" "}
                  {formatBRL(summary.openCents)}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <GnreSettingsDialog open={settings} onClose={() => setSettings(false)} />
    </>
  );
}
